/**
 * `sceneWire.ts` — conversions between the web scene state and Unity's
 * **JsonUtility event wire** shape of `SceneData` (`Models/Data/SceneData.cs`),
 * carried by `create_scene` / `update_scene` (`SceneEventData.sceneData`,
 * tc 31).
 *
 * Three encodings of one scene exist and must not be mixed up:
 *  1. `SceneInfo`  — the web's live UI shape (`sceneInfo.ts`).
 *  2. `SceneDto`   — the Dynamo row (PascalCase attrs, `M{x,y,z}` vectors,
 *                    ISO timestamps, flattened `Skybox*` attrs).
 *  3. wire         — JsonUtility private-field names (camelCase, vectors as
 *                    `[{name,val}]` pair arrays, unix-milli timestamps,
 *                    NESTED `skybox` object).
 *
 * Unity receivers replace their ENTIRE in-memory `SceneData` from the wire
 * payload (`SceneInfo.Set`, `ProjectModelSlim.ApplySceneChanges`), so the
 * outbound builder must emit every field. Modeled fields come from the live
 * `SceneInfo`; fields the web doesn't model (description, scene type/mode,
 * tracking marker, origin transform, …) pass through from the retained raw
 * `SceneDto` so a web-originated event never wipes them on Unity peers.
 *
 * The scene VIEW moved out of that pass-through list when Set Scene View
 * landed: it is modeled now (`SceneInfo.sceneView`) and must be emitted from
 * `info`, or a local Set Scene View would broadcast the pose it replaced and
 * every peer would keep the stale one.
 *
 * `m_actionDatas` is intentionally absent: `ActionData` is not
 * `[Serializable]`, so Unity's own JsonUtility payloads omit it too —
 * actions never travel over `update_scene`.
 */

import type { SceneDto } from "./sceneDto";
import type { SceneInfo } from "./sceneInfo";
import { toNameFloatMap } from "./nameFloatMap";

type WirePair = { readonly name: string; readonly val: number };

/** `{x:1,y:2}`-style record → JsonUtility `NameFloatPair[]` (ordered keys). */
function recordToPairs(
  value: unknown,
  keys: ReadonlyArray<string>,
): WirePair[] {
  const map = toNameFloatMap(value);
  return keys
    .filter((key) => typeof map[key] === "number")
    .map((key) => ({ name: key, val: map[key] as number }));
}

/**
 * Like {@link recordToPairs} but ALWAYS emits every key, defaulting missing
 * axes. Unity's `SceneData.OnAfterDeserialize` indexes the rebuilt
 * dictionaries by key (`Rotation["x"]`, …), so a partial/empty pair array
 * on the wire throws `KeyNotFoundException` inside the receiver's apply.
 */
function recordToPairsWithDefaults(
  value: unknown,
  keys: ReadonlyArray<string>,
  fallback = 0,
): WirePair[] {
  const map = toNameFloatMap(value);
  return keys.map((key) => ({
    name: key,
    val: typeof map[key] === "number" ? (map[key] as number) : fallback,
  }));
}

function vec3ToPairs(value: readonly [number, number, number]): WirePair[] {
  return [
    { name: "x", val: value[0] },
    { name: "y", val: value[1] },
    { name: "z", val: value[2] },
  ];
}

/** JsonUtility `NameFloatPair[]` (or `{x,y,z}` record) → Dynamo-style record. */
function pairsToRecord(value: unknown): Record<string, number> | undefined {
  if (value === undefined || value === null) return undefined;
  const map = toNameFloatMap(value);
  return Object.keys(map).length > 0 ? { ...map } : {};
}

/** ISO-8601 (or empty) → unix millis for the wire `modifiedTimestamp`. */
function isoToMillis(iso: string | undefined): number {
  if (!iso) return 0;
  const parsed = Date.parse(iso);
  return Number.isFinite(parsed) ? parsed : 0;
}

function millisToIso(value: unknown): string | undefined {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return undefined;
  }
  return new Date(value).toISOString();
}

function toNumber(value: unknown, fallback: number): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

function toBool(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function toStr(value: unknown, fallback: string): string {
  return typeof value === "string" ? value : fallback;
}

function toStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

/**
 * Build the full JsonUtility `SceneData` wire object for an outbound
 * `create_scene` / `update_scene`. Modeled fields come from `info`,
 * unmodeled ones pass through from the retained raw Dynamo row (`dto`).
 * Absent DTO attrs fall back to Unity's `SceneData` constructor defaults.
 */
export function buildWireSceneData(
  info: SceneInfo,
  dto: SceneDto | undefined,
): Record<string, unknown> {
  return {
    name: info.name,
    id: info.id,
    // Unity's `Index` is a SPARSE SORT KEY (first scene 0, appends +100,
    // midpoint inserts), NOT a list position. The retained row keeps the
    // authoritative key; the reindexed web display position is only a
    // fallback for rows created before retention existed.
    index: toNumber(dto?.Index, info.index),
    thumbnailID: toStr(dto?.ThumbnailID, ""),
    smoothTransition: toBool(dto?.SmoothTransition, false),
    presenterNotes: info.presenterNotes,
    sceneDescription: toStr(dto?.SceneDescription, ""),
    sceneDescriptionVisible: toBool(dto?.SceneDescriptionVisible, false),
    defaultSkybox: toStr(dto?.DefaultSkybox, ""),
    assetIDs: [...info.assetIds],
    mediaAssetIDs: [...info.mediaAssetIds],
    modifiedTimestamp: isoToMillis(info.modifiedAt),
    versionTag: toStr(dto?.VersionTag, ""),
    sceneType: toNumber(dto?.SceneType, 0),
    sceneMode: toNumber(dto?.SceneMode, 0),
    usePartColor: info.cuttingPlane.usePartColor,
    trs: info.transitionSpeed,
    isMarkerAlwaysVisible: toBool(dto?.IsMarkerAlwaysVisible, true),
    trackingMarkerID: toNumber(dto?.TrackingMarkerID, -1),
    shouldSnapback: info.shouldSnapback,
    trackingMarkerRotation: recordToPairsWithDefaults(dto?.TrackingMarkerRotation, ["x", "y", "z"]),
    trackingMarkerPosition: recordToPairsWithDefaults(dto?.TrackingMarkerPosition, ["x", "y", "z"]),
    trackingReferenceEntityID: toStr(dto?.TrackingReferenceEntityID, ""),
    cuttingPlaneVisible: info.cuttingPlane.visible,
    cuttingPlaneExist: info.cuttingPlane.exist,
    cuttingPlaneRotation: vec3ToPairs(info.cuttingPlane.euler),
    cuttingPlanePosition: vec3ToPairs(info.cuttingPlane.position),
    // Scene view rides `info`, not the retained row: a local Set Scene View
    // applies to `SceneInfo` first, so reading the DTO here would broadcast
    // the PREVIOUS view and every peer would keep the stale one.
    // `sceneViewRotation` carries the orbit PIVOT — see `SceneViewState`.
    isViewOrthographic: info.sceneView.isOrthographic,
    sceneViewRotation: vec3ToPairs(info.sceneView.pivot),
    sceneViewPosition: vec3ToPairs(info.sceneView.position),
    rotation: recordToPairsWithDefaults(dto?.Rotation, ["x", "y", "z"]),
    translation: recordToPairsWithDefaults(dto?.Translation, ["x", "y", "z"]),
    scaleFactor: toNumber(dto?.ScaleFactor, 1),
    grid: info.gridVisible,
    // `info.shadowsVisibleMask` already carries the legacy-bool fallback
    // (applied with Unity's precedence in `mapSceneDtoToInfo`) — a second
    // fallback here would flip explicitly-off shadows back to All.
    shadow: info.shadowsVisibleMask,
    console: info.consoleVisible,
    table: info.tableVisible,
    light: info.lightMode,
    skybox: {
      mode: info.skybox.mode,
      solidColor: recordToPairs(info.skybox.solidColor, ["r", "g", "b", "a"]),
      customColor: recordToPairs(info.skybox.customColor, ["r", "g", "b", "a"]),
      rotation: info.skybox.rotation,
      intensity: info.skybox.intensity,
      scale: recordToPairs(info.skybox.scale, ["x", "y"]),
      modifiedTimestamp: isoToMillis(info.skybox.modifiedAt),
      textureID: info.skybox.textureId,
      format: info.skybox.format,
    },
    hiddenObjectMode: info.hiddenObjectMode === "hidden" ? 1 : 0,
    isolatedEntityIDs: [...info.isolatedSnapshotIds],
    enableCollideVisualization: toBool(dto?.EnableCollideVisualization, false),
    enableForceVisualization: toBool(dto?.EnableForceVisualization, false),
    enableGrabIndicators: toBool(dto?.EnableGrabIndicators, false),
    selectedVisualizationType: toNumber(dto?.SelectedVisualizationType, 0),
  };
}

/**
 * Merge an inbound wire `SceneData` payload onto the retained raw Dynamo
 * row, producing a full `SceneDto` that `mapSceneDtoToInfo` can turn back
 * into a `SceneInfo`. Fields absent from the payload keep their base
 * value (a real Unity JsonUtility payload always carries every field, but
 * partial tolerance keeps us robust against older senders).
 *
 * Returns `null` when the payload has no usable scene id.
 */
export function mergeWireSceneDataIntoDto(
  wire: Record<string, unknown>,
  base: SceneDto | undefined,
  fallbackSceneId?: string,
): SceneDto | null {
  const id = toStr(wire.id, "") || base?.ID || fallbackSceneId || "";
  if (!id) return null;

  const merged: Record<string, unknown> = { ...(base ?? {}), ID: id };
  const set = (attr: string, value: unknown): void => {
    if (value !== undefined) merged[attr] = value;
  };

  if (typeof wire.name === "string") set("Name", wire.name);
  if (typeof wire.index === "number") set("Index", wire.index);
  if (typeof wire.thumbnailID === "string") set("ThumbnailID", wire.thumbnailID);
  set("ModifiedTimestamp", millisToIso(wire.modifiedTimestamp));
  if (typeof wire.smoothTransition === "boolean") set("SmoothTransition", wire.smoothTransition);
  if (typeof wire.presenterNotes === "string") set("PresenterNotes", wire.presenterNotes);
  if (typeof wire.sceneDescription === "string") set("SceneDescription", wire.sceneDescription);
  if (typeof wire.sceneDescriptionVisible === "boolean") {
    set("SceneDescriptionVisible", wire.sceneDescriptionVisible);
  }
  if (typeof wire.defaultSkybox === "string") set("DefaultSkybox", wire.defaultSkybox);
  if (Array.isArray(wire.assetIDs)) set("AssetIDs", toStringArray(wire.assetIDs));
  if (Array.isArray(wire.mediaAssetIDs)) set("MediaAssetIDs", toStringArray(wire.mediaAssetIDs));
  if (typeof wire.versionTag === "string") set("VersionTag", wire.versionTag);
  if (typeof wire.sceneType === "number") set("SceneType", wire.sceneType);
  if (typeof wire.sceneMode === "number") set("SceneMode", wire.sceneMode);
  if (typeof wire.usePartColor === "boolean") set("UsePartColor", wire.usePartColor);
  if (typeof wire.trs === "number") set("TransitionSpeed", wire.trs);
  if (typeof wire.isMarkerAlwaysVisible === "boolean") {
    set("IsMarkerAlwaysVisible", wire.isMarkerAlwaysVisible);
  }
  if (typeof wire.trackingMarkerID === "number") set("TrackingMarkerID", wire.trackingMarkerID);
  if (typeof wire.shouldSnapback === "boolean") set("ShouldSnapback", wire.shouldSnapback);
  set("TrackingMarkerRotation", pairsToRecord(wire.trackingMarkerRotation));
  set("TrackingMarkerPosition", pairsToRecord(wire.trackingMarkerPosition));
  if (typeof wire.trackingReferenceEntityID === "string") {
    set("TrackingReferenceEntityID", wire.trackingReferenceEntityID);
  }
  if (typeof wire.cuttingPlaneVisible === "boolean") set("CuttingPlaneVisible", wire.cuttingPlaneVisible);
  if (typeof wire.cuttingPlaneExist === "boolean") set("CuttingPlaneExist", wire.cuttingPlaneExist);
  set("CuttingPlaneRotation", pairsToRecord(wire.cuttingPlaneRotation));
  set("CuttingPlanePosition", pairsToRecord(wire.cuttingPlanePosition));
  if (typeof wire.isViewOrthographic === "boolean") set("IsViewOrthographic", wire.isViewOrthographic);
  set("SceneViewRotation", pairsToRecord(wire.sceneViewRotation));
  set("SceneViewPosition", pairsToRecord(wire.sceneViewPosition));
  set("Rotation", pairsToRecord(wire.rotation));
  set("Translation", pairsToRecord(wire.translation));
  if (typeof wire.scaleFactor === "number") set("ScaleFactor", wire.scaleFactor);
  if (typeof wire.grid === "boolean") set("GridVisible", wire.grid);
  if (typeof wire.shadow === "number") set("ShadowsVisiblePerDevice", wire.shadow);
  if (typeof wire.console === "boolean") set("ConsoleVisible", wire.console);
  if (typeof wire.table === "boolean") set("TableVisible", wire.table);
  if (typeof wire.light === "number") set("LightMode", wire.light);
  if (typeof wire.hiddenObjectMode === "number") set("ShowHiddenAs", wire.hiddenObjectMode);
  if (Array.isArray(wire.isolatedEntityIDs)) {
    set("IsolatedEntityIDs", toStringArray(wire.isolatedEntityIDs));
  }
  if (typeof wire.enableCollideVisualization === "boolean") {
    set("EnableCollideVisualization", wire.enableCollideVisualization);
  }
  if (typeof wire.enableForceVisualization === "boolean") {
    set("EnableForceVisualization", wire.enableForceVisualization);
  }
  if (typeof wire.enableGrabIndicators === "boolean") {
    set("EnableGrabIndicators", wire.enableGrabIndicators);
  }
  if (typeof wire.selectedVisualizationType === "number") {
    set("SelectedVisualizationType", wire.selectedVisualizationType);
  }

  const skybox = wire.skybox;
  if (skybox && typeof skybox === "object") {
    const sky = skybox as Record<string, unknown>;
    if (typeof sky.mode === "number") set("SkyboxMode", sky.mode);
    set("SolidColor", pairsToRecord(sky.solidColor));
    set("CustomColor", pairsToRecord(sky.customColor));
    if (typeof sky.rotation === "number") set("SkyboxRotation", sky.rotation);
    if (typeof sky.intensity === "number") set("SkyboxIntensity", sky.intensity);
    set("SkyboxScale", pairsToRecord(sky.scale));
    if (typeof sky.textureID === "string") set("SkyboxTextureID", sky.textureID);
    if (typeof sky.format === "string") set("SkyboxFormat", sky.format);
    set("SkyboxTimestamp", millisToIso(sky.modifiedTimestamp));
  }

  return merged as SceneDto;
}

/**
 * Write the `SceneInfo`-modeled fields back onto the raw Dynamo row shape.
 * Used to keep the retained `SceneDto` fresh after local mutations (the
 * DTO is the passthrough source for wire fields the web doesn't model, and
 * the clone source for scene duplication).
 */
export function mergeInfoIntoDto(
  dto: SceneDto | undefined,
  info: SceneInfo,
): SceneDto {
  const merged: Record<string, unknown> = {
    ...(dto ?? {}),
    ID: info.id,
    Name: info.name,
    // `Index` is deliberately NOT synced from `info.index`: the row keeps
    // Unity's sparse SORT KEY, while `info.index` is the reindexed 1-based
    // display position. Order-changing paths (`reorderScenes`,
    // `applySceneChangesFromWire`) update the key explicitly.
    ModifiedTimestamp: info.modifiedAt,
    PresenterNotes: info.presenterNotes,
    AssetIDs: [...info.assetIds],
    MediaAssetIDs: [...info.mediaAssetIds],
    TransitionSpeed: info.transitionSpeed,
    ShowHiddenAs: info.hiddenObjectMode === "hidden" ? 1 : 0,
    IsolatedEntityIDs: [...info.isolatedSnapshotIds],
    GridVisible: info.gridVisible,
    ShadowsVisiblePerDevice: info.shadowsVisibleMask,
    ConsoleVisible: info.consoleVisible,
    TableVisible: info.tableVisible,
    LightMode: info.lightMode,
    SkyboxMode: info.skybox.mode,
    SolidColor: { ...info.skybox.solidColor },
    CustomColor: { ...info.skybox.customColor },
    SkyboxRotation: info.skybox.rotation,
    SkyboxIntensity: info.skybox.intensity,
    SkyboxScale: { ...info.skybox.scale },
    SkyboxTextureID: info.skybox.textureId,
    SkyboxFormat: info.skybox.format,
    SkyboxTimestamp: info.skybox.modifiedAt,
    CuttingPlaneVisible: info.cuttingPlane.visible,
    CuttingPlaneExist: info.cuttingPlane.exist,
    CuttingPlanePosition: {
      x: info.cuttingPlane.position[0],
      y: info.cuttingPlane.position[1],
      z: info.cuttingPlane.position[2],
    },
    CuttingPlaneRotation: {
      x: info.cuttingPlane.euler[0],
      y: info.cuttingPlane.euler[1],
      z: info.cuttingPlane.euler[2],
    },
    UsePartColor: info.cuttingPlane.usePartColor,
    ShouldSnapback: info.shouldSnapback,
    SceneViewPosition: {
      x: info.sceneView.position[0],
      y: info.sceneView.position[1],
      z: info.sceneView.position[2],
    },
    // Orbit PIVOT, under Unity's misleading attribute name — see `SceneViewState`.
    SceneViewRotation: {
      x: info.sceneView.pivot[0],
      y: info.sceneView.pivot[1],
      z: info.sceneView.pivot[2],
    },
    IsViewOrthographic: info.sceneView.isOrthographic,
  };
  return merged as SceneDto;
}
